import { z } from 'zod';
import { costEntryTypeSchema } from './costing';

/**
 * Cost-data integrations (docs/cost-editing-design.md section 11.4: "the
 * `CostAdapter` capability", built as part of `docs/decisions.md`'s Phase
 * 17 generalization).
 *
 * A sibling domain, not an extension of the ops one. Every adapter shares
 * one container type, `Adapter<TSettings, TRow>`
 * (packages/integrations/src/adapter.ts), but `TRow` is never shared: an ops
 * adapter normalizes into `ServiceObservation`, forcing cost data through
 * that same shape would mean two unrelated internal models sharing one
 * contract, which is exactly what principle 5.3 asks adapters to keep apart.
 * `NormalizedCostEntry` below is the cost domain's own row type instead;
 * everything else that isn't domain-shaped -- credential kinds, the
 * encrypted storage, the HTTP client, the `Integration` row a cost source
 * attaches to -- is reused unchanged.
 */

export const COST_PROVIDERS = ['ms-graph-excel'] as const;
export type CostProvider = (typeof COST_PROVIDERS)[number];
export const costProviderSchema = z.enum(COST_PROVIDERS);

/* -------------------------------------------------------------------------- */
/* Per-provider configuration                                                  */
/* -------------------------------------------------------------------------- */

/**
 * Which worksheet to read, and which column holds which fact.
 *
 * A column mapping rather than a fixed layout: every customer's Excel export
 * names and orders its columns differently, and that is configuration, not
 * code (architecture documentation section 5.6) -- the alternative is a new
 * adapter per spreadsheet layout, which does not scale past the second
 * customer.
 */
export const msGraphExcelConfigSchema = z
  .object({
    tenantId: z.string().min(1).max(128),
    /** The OneDrive/SharePoint drive the workbook lives on. */
    driveId: z.string().min(1).max(256),
    /** The workbook file itself. */
    itemId: z.string().min(1).max(256),
    worksheetName: z.string().min(1).max(200),
    /**
     * A named table on the worksheet, preferred when the workbook defines
     * one: Graph can address it directly, and its column headers are stable
     * even if someone inserts a row above the data. Falls back to the
     * worksheet's used range when omitted.
     */
    tableName: z.string().max(200).optional(),
    columns: z.object({
      accountCode: z.string().min(1).max(64),
      entryType: z.string().min(1).max(64),
      amountMinor: z.string().min(1).max(64),
      effectiveOn: z.string().min(1).max(64),
      externalRef: z.string().min(1).max(64),
      description: z.string().max(64).optional(),
      documentRef: z.string().max(64).optional(),
      supplierLabel: z.string().max(64).optional(),
      contractRef: z.string().max(64).optional(),
    }),
    timeoutMs: z.number().int().min(500).max(60_000).default(15_000),
  })
  .strict();
export type MsGraphExcelConfig = z.infer<typeof msGraphExcelConfigSchema>;

export const costIntegrationConfigSchema = z.discriminatedUnion('provider', [
  z.object({ provider: z.literal('ms-graph-excel'), settings: msGraphExcelConfigSchema }),
]);
export type CostIntegrationConfig = z.infer<typeof costIntegrationConfigSchema>;

/* -------------------------------------------------------------------------- */
/* Normalized rows                                                             */
/* -------------------------------------------------------------------------- */

/**
 * One movement, in the dashboard's own vocabulary, whichever provider it came
 * from. Deliberately the same shape `cost-import.service.ts`'s `ParsedRow`
 * already reads into `CostEntry` -- a paste and a sync are the same problem
 * arriving through a different door, so they end at the same normalized row.
 */
export const normalizedCostEntrySchema = z.object({
  /** Identifies this row in the provider's own namespace; the upsert key. */
  externalRef: z.string().min(1).max(255),
  /**
   * A content hash or provider revision. Comparing it against the stored
   * value is what makes "an unchanged row costs no write" possible (the
   * `CostEntry.externalRevision` comment, schema.prisma).
   */
  externalRevision: z.string().min(1).max(255),
  accountCode: z.string().min(1).max(120),
  entryType: costEntryTypeSchema,
  amountMinor: z.union([z.bigint(), z.number().int()]),
  effectiveOn: z.union([z.string(), z.date()]),
  description: z.string().max(2000).nullish(),
  documentRef: z.string().max(120).nullish(),
  supplierLabel: z.string().max(200).nullish(),
  contractRef: z.string().max(120).nullish(),
});
export type NormalizedCostEntry = z.infer<typeof normalizedCostEntrySchema>;

/* Credentials are the same union `integrations.ts` already declares -- an
 * OAuth2 client-credentials grant for MS Graph, and room for whatever SAP or
 * M-Files turn out to need without a second schema to keep in sync. Nothing
 * further to export here; import `integrationCredentialSchema` directly. */
